/**
 * Where the bench comes to rest.
 *
 * Setting up is a statics problem, so this is one line: solve it. Every edit
 * ends here, and what comes back is either the arrangement that stands — with
 * the positions the solve found — or, when no such arrangement exists, exactly
 * what went in, with `diagnose` left to say why.
 *
 * ## What used to be here, and why none of it is
 *
 * This module was six functions and a search. It swung parts plumb under their
 * ropes, ran the dynamics forward to the first thing that caught a falling
 * body, and then hill-climbed on mass-weighted DEPTH to find where a body slung
 * between two points hangs — each of them a different way of guessing at a
 * position the engine could not solve for.
 *
 * The depth search is worth a note, because it was the closest to right and
 * still wrong. "Lowest arrangement" is a real principle: a bench at rest under
 * gravity alone sits at a stationary point of its potential energy. But it is
 * only true under gravity alone — a hand pulling on a rope is not in it — it
 * finds stable equilibria and misses every other kind, and it cannot tell a
 * body swinging under its rope from a machine running, because downhill along
 * the constraint surface is both. The comment that used to stand here said so
 * itself, and said the real answer needed a solved bench, which during setup
 * there was not.
 *
 * There is now. `equilibrium` solves positions and forces together, so a part's
 * sideways place is an output like any other, and every one of those guesses
 * can be deleted rather than corrected.
 *
 * Its own module, and not part of `edit.ts`, because JOINING has to end
 * standing too and `join.ts` is imported BY `edit.ts`. Putting it here is what
 * lets both call it without a cycle.
 */
import { equilibrium } from './equilibrium';
import { remeasure } from './join';
import { World } from './types';

/**
 * Every edit leaves the bench STANDING.
 *
 * `remeasure` first, because an edit can move a part's ports — changing a
 * weight's mass changes how big it is drawn, which moves the hook on its top
 * face — and the spans have to be re-measured before anything is asked about
 * them. Then the solve.
 *
 * This is the tail of every edit rather than something a few of them remember
 * to do. It was on `pin` and on `setSlack` and nowhere else, so changing a
 * mass left rope hanging that nothing ever took up, and the bench reported a
 * load of 19.6 N as held by a rope carrying nothing.
 */
export const stands = (w: World): World => settle(remeasure(w));

/**
 * Put the bench where the statics puts it.
 *
 * `equilibrium` hands back the world it went in with when there is no answer,
 * so a bench that cannot stand is left exactly as the reader arranged it and
 * is reported rather than rearranged. Nothing here needs to know which of
 * those happened.
 */
export const settle = (w: World): World => equilibrium(w).world;
